import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { X, User, Phone, Mail, Building2, MapPin, CreditCard, ChevronDown, ChevronUp } from 'lucide-react';

interface AddClientForm {
  name: string;
  email: string;
  phone: string;
  gstNumber: string;
  panNumber: string;
  currency: string;
  paymentTerms: string;
  creditLimit: string;
  openingBalance: string;
  billingStreet: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  billingCountry: string;
}

const INITIAL_FORM: AddClientForm = {
  name: '',
  email: '',
  phone: '',
  gstNumber: '',
  panNumber: '',
  currency: 'INR',
  paymentTerms: 'Net 30',
  creditLimit: '',
  openingBalance: '',
  billingStreet: '',
  billingCity: '',
  billingState: '',
  billingZip: '',
  billingCountry: '',
};

interface AddClientModalProps {
  onClose: () => void;
}

export function AddClientModal({ onClose }: AddClientModalProps) {
  const [form, setForm] = useState<AddClientForm>(INITIAL_FORM);
  const [showAddress, setShowAddress] = useState(false);
  const [showFinancial, setShowFinancial] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: AddClientForm) => {
      const payload = {
        name: data.name,
        email: data.email || undefined,
        phone: data.phone || undefined,
        gstNumber: data.gstNumber,
        panNumber: data.panNumber,
        currency: data.currency,
        paymentTerms: data.paymentTerms,
        creditLimit: data.creditLimit ? parseFloat(data.creditLimit) : 0,
        openingBalance: data.openingBalance ? parseFloat(data.openingBalance) : 0,
        billingAddress: {
          street: data.billingStreet,
          city: data.billingCity,
          state: data.billingState,
          zipCode: data.billingZip,
          country: data.billingCountry,
        },
      };
      const res = await api.post('/api/sales-ops/clients-prospects', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients-prospects'] });
      onClose();
    },
  });

  const set = (field: keyof AddClientForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    mutation.mutate(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div
        className="bg-card rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card z-10 flex items-center justify-between px-6 py-4 border-b border-border rounded-t-card">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-info/10 flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-on-surface">Add New Client</h2>
              <p className="text-xs text-on-surface-subtle">Details will be saved to your client database</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-on-surface-subtle hover:text-on-surface-variant hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Error */}
          {mutation.isError && (
            <div className="bg-destructive/10 border border-red-100 text-destructive text-sm rounded-lg px-4 py-3">
              {(mutation.error as any)?.response?.data?.error || 'Failed to create client. Please try again.'}
            </div>
          )}

          {/* Basic Info */}
          <section className="space-y-3">
            <h3 className="text-xs font-semibold text-on-surface-subtle uppercase tracking-wider">Basic Information</h3>

            <div>
              <label className="block text-sm font-medium text-on-surface-variant mb-1">
                Client Name <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-on-surface-subtle" />
                <input
                  type="text"
                  placeholder="e.g. Acme Corporation"
                  value={form.name}
                  onChange={set('name')}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-on-surface-subtle" />
                  <input
                    type="email"
                    placeholder="client@example.com"
                    value={form.email}
                    onChange={set('email')}
                    className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Phone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-on-surface-subtle" />
                  <input
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={form.phone}
                    onChange={set('phone')}
                    className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">GST Number</label>
                <input
                  type="text"
                  placeholder="22AAAAA0000A1Z5"
                  value={form.gstNumber}
                  onChange={set('gstNumber')}
                  className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all uppercase"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">PAN Number</label>
                <input
                  type="text"
                  placeholder="AAAAA0000A"
                  value={form.panNumber}
                  onChange={set('panNumber')}
                  className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all uppercase"
                />
              </div>
            </div>
          </section>

          {/* Billing Address — collapsible */}
          <section className="border border-border rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowAddress(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3 bg-muted hover:bg-muted transition-colors text-sm font-medium text-on-surface-variant"
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-on-surface-subtle" />
                Billing Address
              </div>
              {showAddress ? <ChevronUp className="w-4 h-4 text-on-surface-subtle" /> : <ChevronDown className="w-4 h-4 text-on-surface-subtle" />}
            </button>
            {showAddress && (
              <div className="p-4 space-y-3">
                <input
                  type="text"
                  placeholder="Street address"
                  value={form.billingStreet}
                  onChange={set('billingStreet')}
                  className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder="City" value={form.billingCity} onChange={set('billingCity')}
                    className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                  <input type="text" placeholder="State" value={form.billingState} onChange={set('billingState')}
                    className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder="ZIP / Postal code" value={form.billingZip} onChange={set('billingZip')}
                    className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                  <input type="text" placeholder="Country" value={form.billingCountry} onChange={set('billingCountry')}
                    className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                </div>
              </div>
            )}
          </section>

          {/* Financial Settings — collapsible */}
          <section className="border border-border rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowFinancial(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3 bg-muted hover:bg-muted transition-colors text-sm font-medium text-on-surface-variant"
            >
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-on-surface-subtle" />
                Financial Settings
              </div>
              {showFinancial ? <ChevronUp className="w-4 h-4 text-on-surface-subtle" /> : <ChevronDown className="w-4 h-4 text-on-surface-subtle" />}
            </button>
            {showFinancial && (
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-on-surface-variant mb-1">Currency</label>
                    <select value={form.currency} onChange={set('currency')}
                      className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all bg-card">
                      <option value="INR">INR – Indian Rupee</option>
                      <option value="USD">USD – US Dollar</option>
                      <option value="EUR">EUR – Euro</option>
                      <option value="GBP">GBP – British Pound</option>
                      <option value="AED">AED – UAE Dirham</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-on-surface-variant mb-1">Payment Terms</label>
                    <select value={form.paymentTerms} onChange={set('paymentTerms')}
                      className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all bg-card">
                      <option>Immediate</option>
                      <option>Net 15</option>
                      <option>Net 30</option>
                      <option>Net 45</option>
                      <option>Net 60</option>
                      <option>Net 90</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-on-surface-variant mb-1">Credit Limit</label>
                    <input type="number" min="0" placeholder="0.00" value={form.creditLimit} onChange={set('creditLimit')}
                      className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                  </div>
                  <div>
                    <label className="block text-xs text-on-surface-variant mb-1">Opening Balance</label>
                    <input type="number" placeholder="0.00" value={form.openingBalance} onChange={set('openingBalance')}
                      className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-on-surface-variant bg-muted hover:bg-muted rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !form.name.trim()}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary-dim disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Saving...
                </>
              ) : (
                'Add Client'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
